import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ApiError, get } from "./api";

export interface DayStreak {
  current: number;
  best: number;
  doneToday: boolean;
  atRisk: boolean;
  restDay: boolean;
  next: { days: number; coins: number };
  week: { day: string; label: string; played: boolean; today: boolean }[];
}

export interface Me {
  id: number;
  role: "student" | "teacher" | "admin";
  username: string;
  realName: string;
  grade: number | null;
  ageBand: string | null;
  xp: number;
  level: number;
  levelProgress: { level: number; into: number; needed: number; pct?: number };
  balance: number;
  title: string | null;
  avatar: Record<string, any>;
  /** the shop item the player is saving up for (v17), or null */
  goal: { id: string; name: string; category: string; price: number; rarity: string; data: Record<string, any> } | null;
  /** v21: finished sub-goals, badges and missions that are waiting to be collected ("Indløs") */
  claimable: number;
  /** v18: "days in a row" (students only) */
  dayStreak: DayStreak | null;
  onboardingStep: number;
  showOnLeaderboards: boolean;
  shareNameWithTeacher: boolean;
  demo: boolean;
  activeMatch: { id: string; game: string; status: string } | null;
}

export interface Toast {
  id: number;
  kind: "ok" | "error" | "info";
  text: string;
}

interface AppCtx {
  me: Me | null;
  loading: boolean;
  online: boolean;
  reload: () => Promise<Me | null>;
  setMe: (m: Me | null) => void;
  toasts: Toast[];
  toast: (text: string, kind?: Toast["kind"]) => void;
  dismiss: (id: number) => void;
}
const Ctx = createContext<AppCtx>(null as unknown as AppCtx);
export const useApp = () => useContext(Ctx);

export function AppProvider({ children }: { children?: any }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  const reload = useCallback(async () => {
    try {
      const r = await get<{ user: Me }>("/api/me");
      setMe(r.user);
      return r.user;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, [reload]);

  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const toast = useCallback(
    (text: string, kind: Toast["kind"] = "info") => {
      const id = ++seq.current;
      setToasts((t) => [...t.filter((x) => !(x.text === text && x.kind === kind)).slice(-3), { id, kind, text }]);
      setTimeout(() => dismiss(id), 5000);
    },
    [dismiss],
  );

  return <Ctx.Provider value={{ me, loading, online, reload, setMe, toasts, toast, dismiss }}>{children}</Ctx.Provider>;
}

/** Small helper: loads data on mount (and when deps change) with loading/error state. */
export function useLoad<T>(fn: () => Promise<T>, deps: readonly any[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const run = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fn());
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, "Noget gik galt."));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, deps);
  useEffect(() => {
    void run();
  }, [run]);
  return { data, error, loading, reload: run, setData };
}
